import { useEffect, useMemo, useState } from 'react';
import { Capture } from './Capture.tsx';
import { Icon } from './Icon.tsx';
import { useApp } from '../app/store.ts';
import { derivePreview, deriveTitle } from '../data/markdown.ts';
import { matches, parseQuery, score } from '../lib/search.ts';
import { searchIds } from '../search/client.ts';
import { dueCount, todos } from '../todos/store.ts';
import type { LocalNote } from '../data/types.ts';

const DAY = 86_400_000;

function groupOf(timestamp: number, now: number): string {
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  if (timestamp >= startOfToday) return 'Heute';
  if (timestamp >= startOfToday - 6 * DAY) return 'Diese Woche';
  if (timestamp >= startOfToday - 30 * DAY) return 'Diesen Monat';
  return 'Älter';
}

function formatDate(timestamp: number, now: number): string {
  const date = new Date(timestamp);
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  if (timestamp >= startOfToday) {
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }
  if (timestamp >= startOfToday - 6 * DAY) return date.toLocaleDateString('de-DE', { weekday: 'short' });
  return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

type Props = { onOpenPalette: () => void };

export function NoteList({ onOpenPalette }: Props): JSX.Element {
  const {
    notes, documents, folders, activeId, query, folderFilter, showTrash, sync, showTodos,
    showArchive, open, setQuery, setFolderFilter, togglePin, create, setShowTodos, setShowArchive,
    showFunde, setShowFunde, funde,
  } = useApp();

  const inboxCount = documents.filter(
    (entry) => entry.dokument.status === 'posteingang' && entry.deletedAt === null,
  ).length;
  const fundeCount = funde.filter((entry) => entry.deletedAt === null).length;

  const now = Date.now();
  const searching = query.trim() !== '';

  // Nur die Zahl der fälligen Erinnerungen, damit die Liste sie anzeigen kann.
  const [dueOpen, setDueOpen] = useState(0);
  useEffect(() => {
    const count = () => void todos.all().then((list) => setDueOpen(dueCount(list)));
    count();
    return todos.onChange(count);
  }, []);
  const parsed = useMemo(() => parseQuery(query), [query]);
  const [hits, setHits] = useState<{ ids: string[]; fuzzy: string[] } | null>(null);

  useEffect(() => {
    if (!searching) {
      setHits(null);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(() => {
      void searchIds(query).then((result) => {
        if (alive) setHits(result);
      });
    }, 120);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query, searching, notes.length]);

  /**
   * Eine Suche für alles: Beim Suchen kommen Dokumente dazu, sonst bleiben sie
   * draußen. `in:archiv` und `in:notizen` grenzen ein.
   */
  const pool = useMemo(() => {
    if (!searching) return notes;
    if (parsed.only === 'archiv') return documents;
    if (parsed.only === 'notizen') return notes;
    return [...notes, ...documents, ...funde];
  }, [notes, documents, funde, searching, parsed.only]);

  const visible = useMemo(() => {
    if (searching && hits) {
      const rank = new Map(hits.ids.map((id, position) => [id, position]));
      return pool
        .filter((note) => rank.has(note.id))
        .filter((note) => folderFilter === null || note.folderId === folderFilter)
        .sort((a, b) => rank.get(a.id)! - rank.get(b.id)!);
    }
    const filtered = pool.filter((note) => {
      if (showTrash !== (note.deletedAt !== null)) return false;
      if (folderFilter !== null && note.folderId !== folderFilter) return false;
      return searching ? matches(note, parsed) : true;
    });
    return filtered.sort((a, b) => {
      const byScore = score(b, parsed) - score(a, parsed);
      if (byScore !== 0) return byScore;
      return b.updatedAt - a.updatedAt;
    });
  }, [pool, parsed, folderFilter, showTrash, searching, hits]);

  const pinned = searching ? [] : visible.filter((note) => note.pinned);
  const rest = searching ? visible : visible.filter((note) => !note.pinned);

  const groups: { label: string; items: LocalNote[] }[] = [];
  if (searching) {
    // Dokumente sind keine Notizen — auch nicht in der Trefferliste. Sie stehen für
    // sich, mit eigener Überschrift, statt zwischen den Notizen aufzutauchen.
    const notizen = rest.filter((note) => !note.dokument && !note.fund);
    const dokumente = rest.filter((note) => note.dokument);
    const gesammelt = rest.filter((note) => note.fund);
    if (notizen.length > 0) groups.push({ label: `${notizen.length} Treffer`, items: notizen });
    if (dokumente.length > 0) {
      groups.push({ label: `${dokumente.length} in den Dokumenten`, items: dokumente });
    }
    if (gesammelt.length > 0) {
      groups.push({ label: `${gesammelt.length} im Gesammelten`, items: gesammelt });
    }
  } else {
    for (const note of rest) {
      const label = groupOf(note.updatedAt, now);
      const last = groups[groups.length - 1];
      if (last && last.label === label) last.items.push(note);
      else groups.push({ label, items: [note] });
    }
  }

  // Höchstens drei zuletzt genutzte Ordner; der Rest liegt in der Palette.
  const recent = useMemo(() => {
    const order = new Map<string, number>();
    for (const note of notes) {
      if (!note.folderId) continue;
      order.set(note.folderId, Math.max(order.get(note.folderId) ?? 0, note.updatedAt));
    }
    return [...folders]
      .sort((a, b) => (order.get(b.id) ?? 0) - (order.get(a.id) ?? 0))
      .slice(0, 3);
  }, [folders, notes]);
  const hidden = folders.length - recent.length;

  function row(note: LocalNote): JSX.Element {
    return (
      <button
        key={note.id}
        className={[
          'row',
          note.id === activeId ? 'row-active' : '',
          note.pinned && !searching ? 'row-pinned' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => open(note.id)}
      >
        <span className="row-title">
          {deriveTitle(note.content)}
          {note.dokument && <span className="row-kind">Archiv</span>}
          <em>{formatDate(note.updatedAt, now)}</em>
        </span>
        <span className="row-preview">
          {derivePreview(note.content) ||
            (note.dokument
              ? note.dokument.erkannt.slice(0, 140).replace(/\s+/g, ' ') || 'Noch kein Text'
              : 'Leere Notiz')}
        </span>
        <span
          className="row-pin-toggle"
          role="button"
          tabIndex={-1}
          onClick={(event) => {
            event.stopPropagation();
            void togglePin(note.id);
          }}
        >
          {note.pinned ? 'lösen' : 'anpinnen'}
        </span>
      </button>
    );
  }

  return (
    <aside className="list">
      <header className="list-head">
        <div className="search-box">
          <Icon name="search" size={15} />
          <input
            className="search"
            type="search"
            placeholder="Suchen"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {/* Der einzige Weg zu Befehlen und Einstellungen. Als bloßer Hinweistext
              war er auf dem Telefon eine Sackgasse — dort gibt es kein ⌘K. */}
          <button className="key key-open" onClick={onOpenPalette} title="Befehle und Einstellungen">
            <span className="on-wide">⌘K</span>
            <span className="on-narrow" aria-hidden="true">
              ···
            </span>
            <span className="sr-only">Befehle und Einstellungen</span>
          </button>
          {/* Zuruf ohne festes Ziel: Der gesprochene Befehl entscheidet, ob daraus
              eine Notiz oder eine Erinnerung wird. */}
          <Capture />
        </div>
      </header>

      {/* Fester Einstieg über der Liste — Erinnerungen sind keine Notiz. */}
      <button
        className={`todos-entry${showTodos ? ' todos-entry-on' : ''}`}
        onClick={() => setShowTodos(!showTodos)}
      >
        Erinnerungen
        {dueOpen > 0 && <em>{dueOpen}</em>}
      </button>

      {/* Zweiter Einstieg: Papierkram, der weder Notiz noch Erinnerung ist. */}
      <button
        className={`todos-entry${showArchive ? ' todos-entry-on' : ''}`}
        onClick={() => setShowArchive(!showArchive)}
      >
        Dokumente
        {inboxCount > 0 && <em>{inboxCount}</em>}
      </button>

      {/* Dritter Einstieg: was der Einlieferer hereingereicht hat. */}
      <button
        className={`todos-entry${showFunde ? ' todos-entry-on' : ''}`}
        onClick={() => setShowFunde(!showFunde)}
      >
        Gesammeltes
        {fundeCount > 0 && <em>{fundeCount}</em>}
      </button>

      {folders.length > 0 && (
        <div className="chips">
          <button
            className={folderFilter === null ? 'chip chip-on' : 'chip'}
            onClick={() => setFolderFilter(null)}
          >
            Alle
          </button>
          {recent.map((folder) => (
            <button
              key={folder.id}
              className={`chip chip-${folder.color ?? 0}${folderFilter === folder.id ? ' chip-on' : ''}`}
              onClick={() => setFolderFilter(folder.id)}
            >
              {folder.name}
            </button>
          ))}
          {hidden > 0 && (
            <button className="chip-more" onClick={onOpenPalette} title="Weitere Ordner">
              +{hidden}
            </button>
          )}
        </div>
      )}

      <div className="rows">
        {searching && hits && hits.ids.length === 0 && hits.fuzzy.length > 0 && (
          <p className="group">Nichts gefunden. Meintest du {hits.fuzzy.join(', ')}?</p>
        )}
        {!searching && showTrash && <p className="group label">Papierkorb · 30 Tage</p>}
        {pinned.length > 0 && !showTrash && (
          <>
            <p className="group label">Angepinnt</p>
            {pinned.map(row)}
          </>
        )}
        {groups.map((group) => (
          <div key={group.label}>
            <p className="group label">{group.label}</p>
            {group.items.map(row)}
          </div>
        ))}
        {visible.length === 0 && (
          <p className="empty">
            {query ? 'Nichts gefunden.' : showTrash ? 'Papierkorb ist leer.' : 'Noch keine Notizen.'}
          </p>
        )}
      </div>

      <Sync sync={sync} onCreate={() => void create()} />
    </aside>
  );
}

/**
 * Zweistufig: Eine Störung spricht in ganzen Worten und bietet eine Handlung an.
 * Ruhe ist nur ein Zeitstempel — bemerkbar nur, wenn man hinsieht.
 */
function Sync({
  sync,
  onCreate,
}: {
  sync: { state: string; pending: number };
  onCreate: () => void;
}): JSX.Element {
  const [seen] = useState(() => new Date());
  const quiet = sync.state === 'idle' || sync.state === 'syncing';

  // Der Abgleichszustand teilt die untere Zeile mit dem Neu-Knopf. ⌘N greift nur in der
  // installierten App — im Browsertab fängt Chrome die Kombination ab, deshalb bleibt
  // hier ein sichtbares Ziel.
  return (
    <footer className="sync">
      {quiet ? (
        <span className="sync-quiet">
          {sync.state === 'syncing'
            ? 'überträgt'
            : `${seen.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} abgeglichen`}
        </span>
      ) : (
        <>
          <span className="sync-dot" />
          <span className="sync-text">
            {sync.state === 'offline' ? 'Offline' : 'Abgleich gestört'}
            {sync.pending > 0 && ` · ${sync.pending} ausstehend`}
          </span>
          <button className="act" onClick={() => void import('../data/sync.ts').then((m) => m.syncNow())}>
            Jetzt abgleichen
          </button>
        </>
      )}
      <button className="sync-new" title="Neue Notiz" onClick={onCreate}>
        +
      </button>
    </footer>
  );
}
